import React, { useState } from 'react';
import { array, bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { propTypes, AVAILABILITY_EXCEPTION_METADATA_ACCESS_TIER } from '../../util/types';
import {
  getSharpHours,
  getStartHours,
  getEndHours,
  isDayMomentInsideRange,
  isTimeMomentInsideRange,
  isInRange,
  isSameDate,
  isSameDay,
  resetToStartOfDay,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
  dateIsAfter,
  findNextBoundary,
  timestampToDate,
  localizeAndFormatTime,
  monthIdStringInTimeZone,
  getMonthStartInTimeZone,
  nextMonthFn,
  prevMonthFn,
  isDate,
} from '../../util/dates';
import { required, bookingDateRequired } from '../../util/validators';
import { exceptionTypesConfig } from '../../marketplace-custom-config';
import {
  FieldTextInput,
  FieldSelect,
  FieldMultiSelectInput,
  FieldRadioButton,
  FieldSingleDatePicker,
  Form,
  PrimaryButton,
} from '../../components';
import moment from 'moment';

import EditLiveRoomSchedulesFormRepeatingDates from './EditLiveRoomSchedulesFormRepeatingDates';
import css from './EditLiveRoomSchedulesForm.module.css';

const TODAY = new Date();
const MAX_AVAILABILITY_EXCEPTIONS_RANGE = 365;

// Date formatting used for placeholder texts:
const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const validateDate = date => (isDate(date) ? date : new Date(date));
const validateDateOfDate = date =>
  date?.date ? (isDate(date.date) ? date : { date: new Date(date.date) }) : { date: null };

// React-dates returns wrapped date objects
const extractDateFromFieldDateInput = dateValue =>
  dateValue && dateValue.date ? dateValue.date : null;

const endOfAvailabilityExceptionRange = (timeZone, date) => {
  return resetToStartOfDay(date, timeZone, MAX_AVAILABILITY_EXCEPTIONS_RANGE - 1);
};

// Ensure that AvailabilityExceptions are in sensible order.
// NOTE: this sorting can only be used for non-paginated list of exceptions (page size is max 100)
const sortExceptionsByStartTime = (a, b) => {
  return new Date(a.attributes.start).getTime() - new Date(b.attributes.start).getTime();
};

// Convert exceptions list to inverted array of time-ranges that are available for new exceptions.
const getAvailableTimeRangesForExceptions = (exceptions, timeZone) => {
  const nextBoundary = findNextBoundary(timeZone, TODAY);
  const lastBoundary = endOfAvailabilityExceptionRange(timeZone, TODAY);

  // If no exceptions, return full time range as free of exceptions.
  if (!exceptions || exceptions.length < 1) {
    return [{ start: nextBoundary, end: lastBoundary }];
  }

  const sortedExceptions = exceptions.sort(sortExceptionsByStartTime);
  const endOfLastException = sortedExceptions[sortedExceptions.length - 1].attributes.end;

  const initialRangeCollection = dateIsAfter(nextBoundary, sortedExceptions[0].attributes.start)
    ? { prevEnd: null, ranges: [] }
    : { prevEnd: nextBoundary, ranges: [] };

  const invertedExceptionList = sortedExceptions.reduce((collection, ex) => {
    if (collection.prevEnd) {
      // If previous exception end was the same moment as the current exceptions' start,
      // there's no time-range available.
      if (isSameDate(collection.prevEnd, ex.attributes.start)) {
        // Update prevEnd
        return { prevEnd: ex.attributes.end, ranges: collection.ranges };
      }

      const nextRange = { start: collection.prevEnd, end: ex.attributes.start };
      return { prevEnd: ex.attributes.end, ranges: [...collection.ranges, nextRange] };
    } else {
      return { prevEnd: ex.attributes.end, ranges: [] };
    }
  }, initialRangeCollection).ranges;

  // Return inverted exceptions list as available times for new exception.
  // In addition, if there is time between last exception's end time and last boundary,
  // add that as a available time range
  return dateIsAfter(endOfLastException, lastBoundary)
    ? invertedExceptionList
    : [...invertedExceptionList, { start: endOfLastException, end: lastBoundary }];
};

// Get available start times for new exceptions on given date.
const getAvailableStartTimes = (date, timeRangesOnSelectedDate, intl, timeZone) => {
  if (timeRangesOnSelectedDate.length === 0 || !timeRangesOnSelectedDate[0] || !date) {
    return [];
  }

  // Ensure 00:00 on correct time zone
  const startOfDate = resetToStartOfDay(date, timeZone);
  const nextDay = resetToStartOfDay(startOfDate, timeZone, 1);

  const allHours = timeRangesOnSelectedDate.reduce((availableHours, t) => {
    // time-range: start and end
    const { start, end } = t;

    // If the date is after start, use the date.
    // Otherwise use the start time.
    const startLimit = dateIsAfter(startOfDate, start) ? startOfDate : start;

    // If the end date is after "the next day", use the next date to get the hours of full day.
    // Otherwise use the end of the timeslot.
    const endLimit = dateIsAfter(end, nextDay) ? nextDay : end;

    const hours = getStartHours(intl, timeZone, startLimit, endLimit);

    return availableHours.concat(hours);
  }, []);

  return allHours;
};

// Return exception's start time, if it happens after the beginning of last day of the time-range
//
// Selected end date: today, startOfSelectedEndDay: 00:00
// Time range starts: 10:00 (today)
// => 10:00 (today)
//
// Selected end date: today, startOfSelectedEndDay: 00:00
// Time range starts: 11:00 (yesterday)
// => 00:00 (today)
const startTimeOrStartOfSelectedEndDay = (selectedStartTimeAsDate, startOfSelectedEndDay) =>
  dateIsAfter(selectedStartTimeAsDate, startOfSelectedEndDay)
    ? selectedStartTimeAsDate
    : startOfSelectedEndDay;

// Return time-range's end, if it becomes before selected end date changes (next 00:00)
//
// Selected end date: today. dayAfterSelectedEnd: 00:00 (tomorrow)
// Time range ends: 12:00 (today)
// => 12:00 (today)
//
// Selected end date: today. dayAfterSelectedEnd: 00:00 (tomorrow)
// Time range ends: 14:00 (tomorrow)
// => 00:00 (tomorrow)
const timeRangeEndOrNextMidnight = (timeRangeEnd, dayAfterSelectedEnd) =>
  dateIsAfter(dayAfterSelectedEnd, timeRangeEnd) ? timeRangeEnd : dayAfterSelectedEnd;

// Get available end times for new exceptions on selected time range.
const getAvailableEndTimes = (
  selectedStartTime,
  selectedEndDate,
  selectedTimeRange,
  intl,
  timeZone
) => {
  if (!selectedTimeRange || !selectedEndDate || !selectedStartTime) {
    return [];
  }

  const timeRangeEnd = selectedTimeRange.end;
  const selectedStartTimeAsDate = timestampToDate(selectedStartTime);
  const isSingleDayRange = isSameDay(selectedStartTimeAsDate, selectedEndDate, timeZone);

  // Midnight of selectedEndDate
  const startOfSelectedEndDate = resetToStartOfDay(selectedEndDate, timeZone);
  // Next midnight after selectedEndDate
  const dayAfterSelectedEndDate = resetToStartOfDay(selectedEndDate, timeZone, 1);

  const limitStart = startTimeOrStartOfSelectedEndDay(
    selectedStartTimeAsDate,
    startOfSelectedEndDate
  );
  const limitEnd = timeRangeEndOrNextMidnight(timeRangeEnd, dayAfterSelectedEndDate);

  const endHours = getEndHours(intl, timeZone, limitStart, limitEnd);
  const sharpHours = getSharpHours(intl, timeZone, limitStart, limitEnd);

  const availableEndHours = isSingleDayRange ? endHours : sharpHours;
  return availableEndHours;
};

// Get time-ranges on given date.
const getTimeRanges = (timeRanges, date, timeZone) => {
  return timeRanges && timeRanges[0] && date
    ? timeRanges.filter(t => isInRange(date, t.start, t.end, 'day', timeZone))
    : [];
};

// Use start date to calculate the first possible start time or times, end date and end time or times.
// If the selected value is passed to function it will be used instead of calculated value.
const getAllTimeValues = (
  intl,
  timeZone,
  timeRanges,
  selectedStartDate,
  selectedStartTime,
  selectedEndDate
) => {
  const startTimes = selectedStartTime
    ? []
    : getAvailableStartTimes(
        selectedStartDate,
        getTimeRanges(timeRanges, selectedStartDate, timeZone),
        intl,
        timeZone
      );

  const startTime = selectedStartTime
    ? selectedStartTime
    : startTimes.length > 0 && startTimes[0] && startTimes[0].timestamp
    ? startTimes[0].timestamp
    : null;

  const startTimeAsDate = startTime ? timestampToDate(startTime) : null;

  // Note: We need to remove 1ms from the calculated endDate so that if the end
  // date would be the next day at 00:00 the day in the form is still correct.
  // Because we are only using the date and not the exact time we can remove the
  // 1ms.
  const endDate = selectedEndDate
    ? selectedEndDate
    : startTimeAsDate
    ? new Date(findNextBoundary(timeZone, startTimeAsDate).getTime() - 1)
    : null;

  const selectedTimeRange = timeRanges.find(t => isInRange(startTimeAsDate, t.start, t.end));

  const endTimes = getAvailableEndTimes(startTime, endDate, selectedTimeRange, intl, timeZone);
  const endTime =
    endTimes.length > 0 && endTimes[0] && endTimes[0].timestamp ? endTimes[0].timestamp : null;

  return { startTime, endDate, endTime, selectedTimeRange };
};

// The date picker reports the first day of the month it moved to: keep the current month in
// the listing's time zone and tell the page, which loads that month's availability.
const onMonthChange = (setCurrentMonth, timeZone, onMonthChanged) => month => {
  const updatedMonth = getMonthStartInTimeZone(month, timeZone);
  setCurrentMonth(updatedMonth);

  if (onMonthChanged) {
    onMonthChanged(monthIdStringInTimeZone(updatedMonth, timeZone));
  }
};

// Months can be stepped from the current one up to the end of the exception range.
const canShowNextMonth = (currentMonth, timeZone) =>
  !dateIsAfter(
    nextMonthFn(currentMonth, timeZone),
    endOfAvailabilityExceptionRange(timeZone, TODAY)
  );
const canShowPreviousMonth = (currentMonth, timeZone) =>
  dateIsAfter(prevMonthFn(currentMonth, timeZone), getMonthStartInTimeZone(TODAY, timeZone));

// Format form's value for the date picker: convert timeOfDay to the local time
const formatFieldDateInput = timeZone => v =>
  v && v.date ? { date: timeOfDayFromTimeZoneToLocal(v.date, timeZone) } : { date: v };

// Parse the date picker's value: convert timeOfDay to the given time zone
const parseFieldDateInput = timeZone => v =>
  v && v.date ? { date: timeOfDayFromLocalToTimeZone(v.date, timeZone) } : v;

// The date picker blocks days outside the available time ranges. It passes each day as a
// Date; the range checks work on moments.
const isDayBlocked = (availableTimeRanges, timeZone) =>
  availableTimeRanges
    ? date => {
        const day = moment(date);
        return !availableTimeRanges.find(timeRange =>
          isDayMomentInsideRange(day, timeRange.start, timeRange.end, timeZone)
        );
      }
    : () => false;

// We need to make sure to block the day if user wants to create
// repeating exception on the date & time in which we already have
// availability exception.
const isRepeatingDayBlocked = (
  exceptionStartDate = new Date(),
  exceptionStartTime,
  exceptionEndTime,
  availabilityExceptions,
  repeatingDates,
  availableTimeRanges,
  timeZone
) =>
  availableTimeRanges
    ? date => {
        // The date picker passes a Date; the checks below work on moments.
        const day = moment(date);
        const INVALID = true;

        // Invalid needs to be true as that represents that the day is
        // invalid (e.g blocked)
        const isSameOrBeforeExceptionStartDate = moment(day).isSameOrBefore(
          moment(exceptionStartDate.date)
        );

        const isInTimeRange = !availableTimeRanges.find(timeRange =>
          isDayMomentInsideRange(day, timeRange.start, timeRange.end, timeZone)
        );
        const isTimeRangeAlreadyTaken = availabilityExceptions.find(
          e =>
            isDayMomentInsideRange(day, e.attributes.start, e.attributes.end, timeZone) &&
            isTimeMomentInsideRange(
              timestampToDate(exceptionStartTime),
              timestampToDate(exceptionEndTime),
              e.attributes.start,
              e.attributes.end
            )
        );
        const isRepeatingDatesAlreadyInUse = repeatingDates.find(
          d => moment(d?.date?.date).format('MM/DD/YYYY') === day.format('MM/DD/YYYY')
        );

        return isSameOrBeforeExceptionStartDate
          ? INVALID
          : isTimeRangeAlreadyTaken
          ? INVALID
          : isRepeatingDatesAlreadyInUse
          ? INVALID
          : isInTimeRange;
      }
    : () => false;

// Helper function, which changes form's state when exceptionStartDate input has been changed
const onExceptionStartDateChange = (value, timeRanges, timeZone, props) => {
  const { intl, form } = props;
  if (!value || !value.date) {
    form.batch(() => {
      form.change('exceptionStartTime', null);
      form.change('exceptionEndDate', { date: null });
      form.change('exceptionEndTime', null);
    });
    return;
  }

  // This callback function (onExceptionStartDateChange) is called from the date picker.
  // It gets raw value as a param - browser's local time instead of time in listing's timezone.
  const startDate = timeOfDayFromLocalToTimeZone(value.date, timeZone);
  const timeRangesOnSelectedDate = getTimeRanges(timeRanges, startDate, timeZone);

  const { startTime, endDate, endTime } = getAllTimeValues(
    intl,
    timeZone,
    timeRangesOnSelectedDate,
    startDate
  );

  form.batch(() => {
    form.change('exceptionStartTime', startTime);
    form.change('exceptionEndDate', { date: endDate });
    form.change('exceptionEndTime', endTime);
  });
};

// Helper function, which changes form's state when exceptionStartTime select has been changed
const onExceptionStartTimeChange = (value, timeRangesOnSelectedDate, timeZone, props) => {
  const { intl, form, values } = props;
  const startDate = values?.exceptionStartDate?.date;
  const selectedDuration = parseFloat(values.duration); // Get the selected duration

  const { endDate } = getAllTimeValues(intl, timeZone, timeRangesOnSelectedDate, startDate, value);

  // Calculate the end time based on the selected duration
  const calculatedEndTime = moment(Number(value)).add(selectedDuration, 'hours');

  form.batch(() => {
    form.change('exceptionEndDate', { date: endDate });

    if (values.duration) {
      form.change('exceptionEndTime', String(new Date(calculatedEndTime).valueOf())); // Set the calculated end time
    }
  });
};

const onDurationChange = (value, timeRangesOnSelectedDate, timeZone, props) => {
  const { intl, form, values } = props;
  const startDate = values.exceptionStartDate.date;
  const selectedDuration = parseFloat(value); // Get the selected duration

  const { endDate } = getAllTimeValues(
    intl,
    timeZone,
    timeRangesOnSelectedDate,
    startDate,
    values.exceptionStartTime
  );

  // Calculate the end time based on the selected duration
  const calculatedEndTime = moment(Number(values.exceptionStartTime)).add(
    selectedDuration,
    'hours'
  );

  form.batch(() => {
    form.change('exceptionEndDate', { date: endDate });
    form.change('exceptionEndTime', String(new Date(calculatedEndTime).valueOf())); // Set the calculated end time
  });
};

//////////////////////////////////////////
// EditLiveRoomSchedulesForm //
//////////////////////////////////////////
const EditLiveRoomSchedulesForm = props => {
  const [currentMonth, setCurrentMonth] = useState(getMonthStartInTimeZone(TODAY, props.timeZone));
  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          className,
          rootClassName,
          form,
          formId,
          disabled,
          handleSubmit,
          intl,
          invalid,
          onMonthChanged,
          timeZone,
          availabilityExceptions: unformattedAvailabilityExceptions,
          addExceptionInProgress,
          updateInProgress,
          fetchErrors,
          values,
          accessOptions,
          membershipTiers,
          durationConfigOptions,
        } = formRenderProps;

        const idPrefix = `${formId}` || 'EditLiveRoomSchedulesForm';

        const {
          exceptionStartDate: unformattedExceptionStartDate,
          exceptionStartTime = null,
          exceptionEndDate: unformattedExceptionEndDate,
          exceptionEndTime,
        } = values;

        const exceptionStartDate = validateDateOfDate(unformattedExceptionStartDate);
        const exceptionEndDate = validateDateOfDate(unformattedExceptionEndDate);

        const exceptionStartDay = extractDateFromFieldDateInput(exceptionStartDate);

        const startTimeDisabled = !exceptionStartDate || !exceptionStartTime || !exceptionEndDate;
        const durationDisabled = !exceptionStartDate || !exceptionStartTime || !exceptionEndDate;

        const availabilityExceptions = unformattedAvailabilityExceptions.map(e => ({
          ...e,
          attributes: {
            ...e.attributes,
            start: validateDate(e.attributes.start),
            end: validateDate(e.attributes.end),
          },
        }));

        // Get all the available time-ranges for creating new AvailabilityExceptions
        const availableTimeRanges = getAvailableTimeRangesForExceptions(
          availabilityExceptions,
          timeZone
        ).map(d => ({
          ...d,
          start: validateDate(d.start),
          end: validateDate(d.end),
        }));

        const timeRangesOnSelectedDate = getTimeRanges(
          availableTimeRanges,
          exceptionStartDay,
          timeZone
        );

        const availableStartTimes = getAvailableStartTimes(
          exceptionStartDay,
          timeRangesOnSelectedDate,
          intl,
          timeZone
        );

        const handleMonthChange = onMonthChange(setCurrentMonth, timeZone, onMonthChanged);
        const monthSteppers = {
          showPreviousMonthStepper: canShowPreviousMonth(currentMonth, timeZone),
          showNextMonthStepper: canShowNextMonth(currentMonth, timeZone),
        };

        const { updateListingError } = fetchErrors || {};

        // title
        const titleMessage = intl.formatMessage({ id: 'EditLiveRoomSchedulesForm.title' });
        const titlePlaceholderMessage = intl.formatMessage({
          id: 'EditLiveRoomSchedulesForm.titlePlaceholder',
        });
        const titleRequiredMessage = intl.formatMessage({
          id: 'EditLiveRoomSchedulesForm.titleRequired',
        });

        // type
        const typeMessage = intl.formatMessage({ id: 'EditLiveRoomSchedulesForm.type' });
        const typePlaceholderMessage = intl.formatMessage({
          id: 'EditLiveRoomSchedulesForm.typePlaceholder',
        });

        const startTimeLabel = intl.formatMessage({
          id: 'EditLiveRoomSchedulesForm.startTimeLabel',
        });
        const startTimePlaceholder = localizeAndFormatTime(
          intl,
          timeZone,
          findNextBoundary(timeZone, TODAY)
        );

        const repeatLabel = intl.formatMessage({ id: 'EditLiveRoomSchedulesForm.repeat' });
        const accessLabel = intl.formatMessage({ id: 'EditLiveRoomSchedulesForm.access' });

        const submitInProgress = updateInProgress || addExceptionInProgress;
        const hasData =
          exceptionStartDate && exceptionStartTime && exceptionEndDate && exceptionEndTime;
        const submitDisabled = !hasData || invalid || disabled || submitInProgress;

        const classes = classNames(rootClassName || css.root, className);

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <h2 className={css.heading}>
              <FormattedMessage id="EditLiveRoomSchedulesForm.heading" />
            </h2>

            <div className={css.section}>
              <FieldTextInput
                id="title"
                name="title"
                type="text"
                label={titleMessage}
                placeholder={titlePlaceholderMessage}
                validate={required(titleRequiredMessage)}
              />

              <div className={classNames(css.formRow, css.timezoneAndDate)}>
                <div className={css.field}>
                  <FieldSelect id="type" name="type" label={typeMessage}>
                    <option value="" disabled>
                      {typePlaceholderMessage}
                    </option>
                    {exceptionTypesConfig.map(t => (
                      <option key={t.key} value={t.key}>
                        {intl.formatMessage({
                          id: `EditLiveRoomSchedulesForm.type.${t.key}`,
                          defaultMessage: t.label,
                        })}
                      </option>
                    ))}
                  </FieldSelect>
                </div>
                <div className={css.field}>
                  <FieldSingleDatePicker
                    className={css.fieldDateInput}
                    id={`${idPrefix}.exceptionStartDate`}
                    name="exceptionStartDate"
                    label={intl.formatMessage({
                      id: 'EditLiveRoomSchedulesForm.startDateLabel',
                    })}
                    placeholderText={intl.formatDate(TODAY, dateFormattingOptions)}
                    format={formatFieldDateInput(timeZone)}
                    parse={parseFieldDateInput(timeZone)}
                    isDayBlocked={isDayBlocked(availableTimeRanges, timeZone)}
                    onChange={value =>
                      onExceptionStartDateChange(
                        value,
                        availableTimeRanges,
                        timeZone,
                        formRenderProps
                      )
                    }
                    onMonthChange={handleMonthChange}
                    {...monthSteppers}
                    useMobileMargins
                    showErrorMessage={false}
                    validate={bookingDateRequired('Required')}
                  />
                </div>
              </div>
              <div className={css.formRow}>
                <div className={css.field}>
                  <FieldSelect
                    name="exceptionStartTime"
                    id={`${idPrefix}.exceptionStartTime`}
                    className={exceptionStartDate?.date ? css.fieldSelect : css.fieldSelectDisabled}
                    selectClassName={exceptionStartDate?.date ? css.select : css.selectDisabled}
                    label={startTimeLabel}
                    disabled={startTimeDisabled}
                    onChange={value =>
                      onExceptionStartTimeChange(
                        value,
                        timeRangesOnSelectedDate,
                        timeZone,
                        formRenderProps
                      )
                    }
                  >
                    {exceptionStartDay ? (
                      availableStartTimes.map(p => (
                        <option key={p.timestamp} value={p.timestamp}>
                          {p.timeOfDay}
                        </option>
                      ))
                    ) : (
                      <option>{startTimePlaceholder}</option>
                    )}
                  </FieldSelect>
                </div>

                <div className={css.field}>
                  <FieldSelect
                    name="duration"
                    id={`${idPrefix}.duration`}
                    label="Duration"
                    disabled={durationDisabled}
                    onChange={value =>
                      onDurationChange(value, timeRangesOnSelectedDate, timeZone, formRenderProps)
                    }
                    validate={required(
                      intl.formatMessage({
                        id: 'EditLiveRoomSchedulesForm.durationRequired',
                      })
                    )}
                  >
                    <option value="" disabled>
                      {durationConfigOptions[0].label}
                    </option>
                    {durationConfigOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FieldSelect>
                </div>
              </div>

              <div className={css.bottomSection}>
                <EditLiveRoomSchedulesFormRepeatingDates
                  // Component props
                  fieldId="repeatingDates"
                  label={repeatLabel}
                  push={form?.mutators?.push}
                  values={values['repeatingDates']}
                  // Date input props
                  placeholderText={intl.formatDate(TODAY, dateFormattingOptions)}
                  format={formatFieldDateInput(timeZone)}
                  parse={parseFieldDateInput(timeZone)}
                  isDayBlocked={isRepeatingDayBlocked(
                    exceptionStartDate,
                    exceptionStartTime,
                    exceptionEndTime,
                    availabilityExceptions,
                    values.repeatingDates,
                    availableTimeRanges,
                    timeZone
                  )}
                  disabled={!exceptionStartDate}
                  onChange={(name, value) => form.change(`${name}.date`, value)}
                  onMonthChange={handleMonthChange}
                  {...monthSteppers}
                />
                <div className={css.accessRadioGroup}>
                  <span className={css.accessRadioLabel}>
                    <FormattedMessage id="EditLiveRoomSchedulesForm.scheduleA" />
                  </span>
                  <div className={css.accessRadioOptions}>
                    <FieldRadioButton
                      id={`${idPrefix}.access.free`}
                      name="access"
                      value="free"
                      label={intl.formatMessage({
                        id: 'EditLiveRoomSchedulesForm.freeEpisode',
                      })}
                    />
                    {membershipTiers && membershipTiers.length > 0 && (
                      <FieldRadioButton
                        id={`${idPrefix}.access.members`}
                        name="access"
                        value="members"
                        label={intl.formatMessage({
                          id: 'EditLiveRoomSchedulesForm.membersOnlyEpisode',
                        })}
                      />
                    )}
                  </div>
                </div>
                {values.access === 'members' && membershipTiers && membershipTiers.length > 0 && (
                  <FieldMultiSelectInput
                    id={`${idPrefix}.membershipTierIds`}
                    name="membershipTierIds"
                    className={css.bottomField}
                    label={intl.formatMessage({
                      id: 'EditLiveRoomSchedulesForm.membershipTierLabel',
                    })}
                    options={membershipTiers.map(tier => ({
                      key: tier.id,
                      label: tier.name,
                    }))}
                    validate={
                      values.access === 'members'
                        ? required(
                            intl.formatMessage({
                              id: 'EditLiveRoomSchedulesForm.membershipTierRequired',
                            })
                          )
                        : null
                    }
                  />
                )}
              </div>
            </div>

            <div className={css.submitButton}>
              {updateListingError ? (
                <p className={css.error}>
                  <FormattedMessage id="EditLiveRoomSchedulesForm.updateFailed" />
                </p>
              ) : null}
              <PrimaryButton
                className={css.exceptionButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="EditLiveRoomSchedulesForm.addException" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

EditLiveRoomSchedulesForm.defaultProps = {
  className: null,
  rootClassName: null,
  fetchErrors: null,
  formId: null,
  availabilityExceptions: [],
  accessOptions: [],
  membershipTiers: [],
};

EditLiveRoomSchedulesForm.propTypes = {
  className: string,
  rootClassName: string,
  formId: string,
  availabilityExceptions: array,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  timeZone: string.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    updateListingError: propTypes.error,
  }),
  accessOptions: array.isRequired,
  membershipTiers: array,
};

export default compose(injectIntl)(EditLiveRoomSchedulesForm);
